import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const ShopCtx = createContext(null);

const EMPTY_CART = { items: [], subtotal: 0, count: 0 };
const EMPTY_WISH = { product_ids: [], products: [], count: 0 };
const EMPTY_CMP = { product_ids: [], products: [], spec_keys: [], count: 0, category_slug: null };

const GUEST_CART_KEY = "vip77_guest_cart";
const GUEST_COMPARE_KEY = "vip77_guest_compare";

// Cart/compare work without an account: guest state lives in localStorage,
// shaped to match what the backend returns for a logged-in user, so Cart.js
// and Compare.js don't need to know which mode they're in. On login, the
// guest cart is merged into the account's cart (see the merge effect below).
function loadGuestCart() {
  try {
    const raw = localStorage.getItem(GUEST_CART_KEY);
    const items = raw ? JSON.parse(raw) : [];
    return Array.isArray(items) ? items : [];
  } catch {
    return [];
  }
}

function saveGuestCart(items) {
  try {
    if (items.length) localStorage.setItem(GUEST_CART_KEY, JSON.stringify(items));
    else localStorage.removeItem(GUEST_CART_KEY);
  } catch {}
}

function buildCartState(items) {
  const subtotal = items.reduce((s, it) => s + it.line_total, 0);
  const count = items.reduce((s, it) => s + it.quantity, 0);
  return { items, subtotal, count };
}

// Mirrors the backend's _hydrate_cart() item shape so a guest cart item looks
// identical to one that came from the API.
function hydrateCartItem(product, variation, quantity) {
  const price = variation.price;
  return {
    product_id: product.id,
    product_slug: product.slug,
    product_name: product.name,
    image: product.images?.[0] || variation.image || variation.images?.[0] || null,
    variation_id: variation.id,
    variation: {
      color: variation.color,
      color_hex: variation.color_hex,
      memory: variation.memory || variation.storage,
      storage: variation.memory || variation.storage,
      region: variation.region,
      warranty: variation.warranty,
      sku: variation.sku,
    },
    price,
    quantity,
    line_total: price * quantity,
  };
}

function loadGuestCompare() {
  try {
    const raw = localStorage.getItem(GUEST_COMPARE_KEY);
    const products = raw ? JSON.parse(raw) : [];
    return Array.isArray(products) ? products : [];
  } catch {
    return [];
  }
}

function saveGuestCompare(products) {
  try {
    if (products.length) localStorage.setItem(GUEST_COMPARE_KEY, JSON.stringify(products));
    else localStorage.removeItem(GUEST_COMPARE_KEY);
  } catch {}
}

function buildCompareState(products) {
  const spec_keys = [];
  for (const p of products) {
    for (const k of Object.keys(p.specs || {})) {
      if (!spec_keys.includes(k)) spec_keys.push(k);
    }
  }
  return {
    product_ids: products.map((p) => p.id),
    products,
    spec_keys,
    count: products.length,
    category_slug: products[0]?.category_slug || null,
  };
}

export function ShopProvider({ children }) {
  const { isAuthed, user } = useAuth();
  const [cart, setCart] = useState(EMPTY_CART);
  const [wishlist, setWishlist] = useState(EMPTY_WISH);
  const [compare, setCompare] = useState(EMPTY_CMP);
  const [categoryNames, setCategoryNames] = useState({});
  const wasAuthed = useRef(isAuthed);

  useEffect(() => {
    api.get("/catalog/categories").then(({ data }) => {
      setCategoryNames(Object.fromEntries((data || []).map((c) => [c.slug, c.name])));
    }).catch(() => {});
  }, []);

  const loadAll = useCallback(async () => {
    if (!isAuthed) {
      setCart(buildCartState(loadGuestCart()));
      setWishlist(EMPTY_WISH);
      setCompare(buildCompareState(loadGuestCompare()));
      return;
    }
    try {
      const [c, w, cmp] = await Promise.all([
        api.get("/cart"),
        api.get("/wishlist"),
        api.get("/compare"),
      ]);
      setCart(c.data);
      setWishlist(w.data);
      setCompare(cmp.data);
    } catch {}
  }, [isAuthed]);

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed, user?.id]);

  // Merge the guest cart and compare list into the account the moment the
  // user logs in, so picking a product before signing in doesn't lose it.
  useEffect(() => {
    if (!wasAuthed.current && isAuthed) {
      const guestItems = loadGuestCart();
      const guestCompareProducts = loadGuestCompare();
      (async () => {
        if (guestItems.length) {
          for (const it of guestItems) {
            try {
              await api.post("/cart/add", {
                product_id: it.product_id,
                variation_id: it.variation_id,
                quantity: it.quantity,
              });
            } catch {}
          }
          saveGuestCart([]);
          try {
            const { data } = await api.get("/cart");
            setCart(data);
          } catch {}
        }
        if (guestCompareProducts.length) {
          try {
            const { data: existing } = await api.get("/compare");
            const existingIds = new Set(existing.product_ids || []);
            for (const p of guestCompareProducts) {
              if (existingIds.has(p.id)) continue; // toggle would remove it - only add what's missing
              try {
                await api.post("/compare/toggle", { product_id: p.id });
              } catch {}
            }
            const { data } = await api.get("/compare");
            setCompare(data);
          } catch {}
          saveGuestCompare([]);
        }
      })();
    }
    wasAuthed.current = isAuthed;
  }, [isAuthed]);

  const ensureAuth = () => {
    if (!isAuthed) {
      toast.error("Войдите в аккаунт, чтобы продолжить");
      return false;
    }
    return true;
  };

  const addToCart = async (product, variationId, quantity = 1) => {
    const productId = typeof product === "string" ? product : product?.id;
    if (!productId) return false;

    if (isAuthed) {
      try {
        const { data } = await api.post("/cart/add", { product_id: productId, variation_id: variationId, quantity });
        setCart(data);
        toast.success("Добавлено в корзину");
        return true;
      } catch {
        toast.error("Не удалось добавить в корзину");
        return false;
      }
    }

    if (typeof product === "string") return false; // guest mode needs the full product to render the item
    const variation = (product.variations || []).find((v) => v.id === variationId);
    if (!variation) return false;

    let items;
    setCart((prev) => {
      items = [...prev.items];
      const idx = items.findIndex((it) => it.product_id === productId && it.variation_id === variationId);
      if (idx >= 0) {
        const newQty = items[idx].quantity + quantity;
        items[idx] = { ...items[idx], quantity: newQty, line_total: items[idx].price * newQty };
      } else {
        items.push(hydrateCartItem(product, variation, quantity));
      }
      saveGuestCart(items);
      return buildCartState(items);
    });
    toast.success("Добавлено в корзину");
    return true;
  };

  const updateCart = async (product_id, variation_id, quantity) => {
    if (isAuthed) {
      try {
        const { data } = await api.post("/cart/update", { product_id, variation_id, quantity });
        setCart(data);
      } catch {}
      return;
    }
    setCart((prev) => {
      let items;
      if (quantity <= 0) {
        items = prev.items.filter((it) => !(it.product_id === product_id && it.variation_id === variation_id));
      } else {
        items = prev.items.map((it) =>
          it.product_id === product_id && it.variation_id === variation_id
            ? { ...it, quantity, line_total: it.price * quantity }
            : it
        );
      }
      saveGuestCart(items);
      return buildCartState(items);
    });
  };

  const removeFromCart = async (product_id, variation_id) => {
    if (isAuthed) {
      try {
        const { data } = await api.post("/cart/remove", { product_id, variation_id, quantity: 0 });
        setCart(data);
      } catch {}
      return;
    }
    setCart((prev) => {
      const items = prev.items.filter((it) => !(it.product_id === product_id && it.variation_id === variation_id));
      saveGuestCart(items);
      return buildCartState(items);
    });
  };

  const clearCart = async () => {
    if (isAuthed) {
      try {
        const { data } = await api.post("/cart/clear");
        setCart(data);
      } catch {}
      return;
    }
    saveGuestCart([]);
    setCart(EMPTY_CART);
  };

  const toggleWishlist = async (product_id) => {
    if (!ensureAuth()) return;
    try {
      const { data } = await api.post("/wishlist/toggle", { product_id });
      setWishlist((w) => ({ ...w, product_ids: data.product_ids, count: data.count }));
      // refresh full wishlist for products data
      const { data: full } = await api.get("/wishlist");
      setWishlist(full);
      toast.success(data.added ? "Добавлено в избранное" : "Удалено из избранного");
    } catch {
      toast.error("Не удалось обновить избранное");
    }
  };

  const toggleCompare = async (productOrId) => {
    const productId = typeof productOrId === "string" ? productOrId : productOrId?.id;
    const product = typeof productOrId === "string" ? null : productOrId;
    if (!productId) return;

    if (isAuthed) {
      try {
        const { data } = await api.post("/compare/toggle", { product_id: productId });
        const { data: full } = await api.get("/compare");
        setCompare(full);
        toast.success(data.added ? "Добавлено к сравнению" : "Убрано из сравнения");
      } catch (e) {
        const msg = e?.response?.data?.detail || "Не удалось обновить сравнение";
        toast.error(typeof msg === "string" ? msg : "Ошибка сравнения");
      }
      return;
    }

    setCompare((prev) => {
      const exists = prev.product_ids.includes(productId);
      if (exists) {
        const products = prev.products.filter((p) => p.id !== productId);
        saveGuestCompare(products);
        toast.success("Убрано из сравнения");
        return buildCompareState(products);
      }
      if (!product) return prev; // removal-only call site (e.g. Compare.js) with just an id
      if (prev.category_slug && prev.category_slug !== product.category_slug) {
        toast.error(`Сравнивать можно только товары одной категории (${categoryNames[prev.category_slug] || prev.category_slug})`);
        return prev;
      }
      if (prev.products.length >= 4) {
        toast.error("Максимум 4 товара в сравнении");
        return prev;
      }
      const products = [...prev.products, product];
      saveGuestCompare(products);
      toast.success("Добавлено к сравнению");
      return buildCompareState(products);
    });
  };

  const clearCompare = async () => {
    if (isAuthed) {
      try {
        await api.post("/compare/clear");
        setCompare(EMPTY_CMP);
      } catch {}
      return;
    }
    saveGuestCompare([]);
    setCompare(EMPTY_CMP);
  };

  return (
    <ShopCtx.Provider
      value={{
        cart,
        wishlist,
        compare,
        addToCart,
        updateCart,
        removeFromCart,
        clearCart,
        toggleWishlist,
        toggleCompare,
        clearCompare,
        reloadCart: async () => {
          if (!isAuthed) {
            setCart(buildCartState(loadGuestCart()));
            return;
          }
          try {
            const { data } = await api.get("/cart");
            setCart(data);
          } catch {}
        },
        isInWishlist: (id) => wishlist.product_ids.includes(id),
        isInCompare: (id) => compare.product_ids.includes(id),
      }}
    >
      {children}
    </ShopCtx.Provider>
  );
}

export const useShop = () => useContext(ShopCtx);
